import {
  isGoodsDerivedPrintSpaceId,
  parseOriginalImageIdFromCropSpaceId,
  resolveGoodsPrintUrl,
} from './goodsPrintUrlResolve';
import i18n from '@/i18n';

function esr(key) {
  return i18n.t(`workbench:exportStampRole.${key}`);
}

/** @typedef {'crop_result' | 'cutout_result' | 'enhance_result' | 'original' | 'direct_upload'} StampSourceKind */

/**
 * @param {string} imageId
 * @returns {StampSourceKind | ''}
 */
export function inferDerivedStampSourceKind(imageId) {
  const id = String(imageId || '').trim();
  if (!id) return '';
  if (/^crop_/i.test(id)) return 'crop_result';
  if (/^cutout_/i.test(id)) return 'cutout_result';
  if (/^enhance_/i.test(id)) return 'enhance_result';
  return '';
}

/**
 * @param {StampSourceKind | string} kind
 * @returns {string}
 */
export function stampSourceKindLabel(kind) {
  switch (String(kind || '').trim()) {
    case 'crop_result':
      return esr('cropResult');
    case 'cutout_result':
      return esr('cutoutResult');
    case 'enhance_result':
      return esr('enhanceResult');
    case 'original':
      return esr('original');
    case 'direct_upload':
      return esr('stampMaterial');
    default:
      return esr('stampMaterial');
  }
}

/**
 * 从印花引用 id 解析血缘原图 id（无派生前缀时返回自身）。
 * 兼容：
 * - crop_CROP-YYYYMMDD-N_{orig}
 * - crop_original_{orig}（裁剪详情桥接原图）
 * - crop_{orig}_{orig}-print-N / crop_{orig}_{orig}-cropped（裁剪详情桥接结果）
 * @param {string} imageId
 * @returns {string}
 */
export function resolveOriginalLineageImageId(imageId) {
  const id = String(imageId || '').trim();
  if (!id) return '';
  if (/^crop_original_/i.test(id)) {
    return id.replace(/^crop_original_/i, '');
  }
  if (/^crop_CROP-\d{8}-\d+_/i.test(id)) {
    return parseOriginalImageIdFromCropSpaceId(id);
  }
  // 裁剪详情：crop_{imageId}_{tileKey}，tileKey 多为 {imageId}-print-N / {imageId}-cropped
  const bridgeCrop = id.match(/^crop_(.+)$/i);
  if (bridgeCrop?.[1]) {
    const rest = bridgeCrop[1];
    const mirrored = rest.match(/^(.+?)_\1-(?:print-\d+|cropped)$/i);
    if (mirrored?.[1]) return mirrored[1];
    const tile = rest.match(/^(.+?)_.+-(?:print-\d+|cropped)$/i);
    if (tile?.[1]) return tile[1];
  }
  if (/^crop_/i.test(id)) return parseOriginalImageIdFromCropSpaceId(id);
  const cutout = id.match(/^cutout_[^_]+_(.+)$/i);
  if (cutout?.[1]) return cutout[1];
  const enhance = id.match(/^enhance_[^_]+_(.+)$/i);
  if (enhance?.[1]) return enhance[1];
  return id;
}

/**
 * @param {string} imageId
 * @param {{
 *   printUrl?: string,
 *   originalUrl?: string,
 *   cropImageUrlMap?: Record<string, string>,
 * }} [ctx]
 * @returns {StampSourceKind}
 */
export function classifyStampSourceKind(imageId, ctx = {}) {
  const derived = inferDerivedStampSourceKind(imageId);
  if (derived) return derived;

  const id = String(imageId || '').trim();
  const printUrl = String(ctx.printUrl || '').trim();
  const originalUrl = String(ctx.originalUrl || '').trim();
  const cropMap = ctx.cropImageUrlMap && typeof ctx.cropImageUrlMap === 'object'
    ? ctx.cropImageUrlMap
    : {};

  if (id && cropMap[id] && printUrl && String(cropMap[id]).trim() === printUrl) {
    return 'crop_result';
  }
  if (printUrl && originalUrl && printUrl === originalUrl) {
    return 'direct_upload';
  }
  if (printUrl && originalUrl && printUrl !== originalUrl) {
    return 'crop_result';
  }
  return 'direct_upload';
}

/**
 * 从图片空间列表取展示 URL（仅用于「原图」列显式解析，不作为印花静默回退）。
 * @param {string} imageId
 * @param {object[]} imageSpaceImages
 * @returns {string}
 */
export function pickImageSpaceUrlById(imageId, imageSpaceImages = []) {
  const id = String(imageId || '').trim();
  if (!id) return '';
  const list = Array.isArray(imageSpaceImages) ? imageSpaceImages : [];
  const img = list.find((im) => String(im?.id) === id);
  const url = img?.dataUrl || img?.url || img?.src || '';
  return String(url || '').trim();
}

/**
 * 组装单张印花的角色元数据与可切换候选（默认激活结果图，不激活原图）。
 * @param {{
 *   name: string,
 *   imageId: string,
 *   printUrl: string,
 *   originalImageId?: string,
 *   originalUrl?: string,
 *   cropImageUrlMap?: Record<string, string>,
 * }} input
 */
export function buildStampRoleMeta(input) {
  const name = String(input?.name || '').trim() || esr('stampName');
  const imageId = String(input?.imageId || '').trim();
  const printUrl = String(input?.printUrl || '').trim();
  const originalImageId = String(input?.originalImageId || resolveOriginalLineageImageId(imageId) || '').trim();
  let originalUrl = String(input?.originalUrl || '').trim();
  if (originalUrl && printUrl && originalUrl === printUrl) {
    // 同源时不重复占「原图」候选，避免误导切换
    if (!isGoodsDerivedPrintSpaceId(imageId) && !input?.cropImageUrlMap?.[imageId]) {
      originalUrl = '';
    }
  }

  const sourceKind = classifyStampSourceKind(imageId, {
    printUrl,
    originalUrl,
    cropImageUrlMap: input?.cropImageUrlMap,
  });

  /** @type {{ url: string, imageId: string, sourceKind: StampSourceKind, label: string }[]} */
  const candidates = [];
  if (printUrl) {
    candidates.push({
      url: printUrl,
      imageId: imageId || originalImageId,
      sourceKind,
      label: stampSourceKindLabel(sourceKind),
    });
  }
  if (originalUrl && originalUrl !== printUrl) {
    candidates.push({
      url: originalUrl,
      imageId: originalImageId || imageId,
      sourceKind: 'original',
      label: stampSourceKindLabel('original'),
    });
  }

  return {
    name,
    imageId,
    url: printUrl,
    activeSourceKind: sourceKind,
    sourceLabel: stampSourceKindLabel(sourceKind),
    printUrl,
    originalUrl: originalUrl && originalUrl !== printUrl ? originalUrl : '',
    originalImageId: originalUrl && originalUrl !== printUrl ? originalImageId : '',
    candidates,
  };
}

/**
 * 将用户切换后的 stampImages 同步回行级 stampImageUrl / stampImageId。
 * @param {object} row
 * @param {object[]} nextStampImages
 */
export function applyStampImagesToExportRow(row, nextStampImages) {
  const stamps = Array.isArray(nextStampImages) ? nextStampImages.filter((s) => s?.url) : [];
  return {
    ...row,
    stampImages: stamps,
    stampImageUrl: stamps[0]?.url || '',
    stampImageId: stamps[0]?.imageId || row?.stampImageId || '',
    stampName: stamps.map((s) => s.name).filter(Boolean).join('+') || row?.stampName || '',
  };
}

/** @typedef {'ai_copywriting' | 'manual' | 'none'} TitleSourceKind */

/**
 * @param {TitleSourceKind | string} kind
 * @returns {string}
 */
export function titleSourceKindLabel(kind) {
  switch (String(kind || '').trim()) {
    case 'ai_copywriting':
      return esr('aiCopywriting');
    case 'manual':
      return esr('manual');
    case 'none':
      return esr('unlinked');
    default:
      return esr('unlinked');
  }
}

/**
 * 标题槽角色元数据：与成果图/印花/原图同属导出显式槽位体系。
 * 选中成果图时默认带出该组成品绑定的 AI 文案；可手工改写。
 *
 * @param {{
 *   title?: string,
 *   language?: string,
 *   sourceKind?: TitleSourceKind,
 *   compositeIds?: string[],
 * }} [input]
 */
export function buildTitleRoleMeta(input = {}) {
  const title = String(input?.title || '').trim();
  const language = String(input?.language || '').trim();
  const compositeIds = (Array.isArray(input?.compositeIds) ? input.compositeIds : [])
    .map((id) => String(id || '').trim())
    .filter(Boolean);

  let sourceKind = String(input?.sourceKind || '').trim();
  if (!sourceKind) {
    sourceKind = title ? 'ai_copywriting' : 'none';
  }
  if (!['ai_copywriting', 'manual', 'none'].includes(sourceKind)) {
    sourceKind = title ? 'ai_copywriting' : 'none';
  }

  return {
    title,
    displayName: title || esr('noTitle'),
    language,
    sourceKind,
    sourceLabel: titleSourceKindLabel(sourceKind),
    editable: true,
    compositeIds,
  };
}

/**
 * 把标题槽写回导出行（导出模板「标题」动态值读 data.title）。
 * @param {object} row
 * @param {{ title?: string, language?: string, sourceKind?: TitleSourceKind }} patch
 */
export function applyTitleRoleToExportRow(row, patch = {}) {
  const prev = row?.titleRole && typeof row.titleRole === 'object' ? row.titleRole : {};
  const nextTitle = patch.title != null ? String(patch.title).trim() : String(prev.title || row?.title || '').trim();
  const nextLanguage = patch.language != null
    ? String(patch.language || '').trim()
    : String(prev.language || row?.titleLanguage || '').trim();
  let sourceKind = patch.sourceKind;
  if (!sourceKind) {
    if (patch.title != null) sourceKind = nextTitle ? 'manual' : 'none';
    else sourceKind = prev.sourceKind || (nextTitle ? 'ai_copywriting' : 'none');
  }
  const titleRole = buildTitleRoleMeta({
    title: nextTitle,
    language: nextLanguage,
    sourceKind,
    compositeIds: prev.compositeIds || row?.compositeIds || [],
  });
  return {
    ...row,
    title: nextTitle || null,
    titleLanguage: nextLanguage || null,
    displayName: titleRole.displayName,
    titleRole,
  };
}

/**
 * @param {string} imageId
 * @param {Record<string, string>} printUrlByImageId
 * @returns {string}
 */
export function resolvePrintUrlAlias(imageId, printUrlByImageId) {
  return resolveGoodsPrintUrl(imageId, printUrlByImageId || {});
}
